import { RefObject } from 'react'
import { IChartBoundaries, IChartInit, TChartExecutableFn } from './chart.types'
import { TDataRef } from '@/services/data/data.types.ts'

/**
 * Базовый класс графика
 * */
class Chart {
  public readonly wrapperRef: RefObject<HTMLDivElement>
  private executable: TChartExecutableFn[]
  public readonly ctx: CanvasRenderingContext2D

  private readonly mouseMoveListeners: Array<(ev: MouseEvent) => void>
  private readonly mouseLeaveListeners: Array<(ev: MouseEvent) => void>
  private animationFrame: number

  public boundaries: IChartBoundaries = defaultBoundaries
  public dataRef: RefObject<TDataRef>
  public frame: number

  private canvasRef: RefObject<HTMLCanvasElement>
  private lastExecute: number
  private live: boolean

  // Временные границы графика
  public tsFrom: number
  public tsTo: number

  constructor(config: IChartInit) {
    for (const key in config) this[key] = config[key]

    this.mouseMoveListeners = []
    this.mouseLeaveListeners = []
    this.executable = []
    this.ctx = config.canvasRef.current.getContext('2d')
    this.tsFrom = config.tsFrom
    this.tsTo = config.tsTo

    requestAnimationFrame(this.execute)

    this.wrapperRef.current.addEventListener('mousemove', (ev) => {
      for (const listener of this.mouseMoveListeners) {
        listener(ev)
      }
    })

    this.wrapperRef.current.addEventListener('mouseleave', (ev) => {
      for (const listener of this.mouseLeaveListeners) {
        listener(ev)
      }
    })
  }

  public addExecutable(fn: TChartExecutableFn) {
    this.executable.push(fn)
  }

  public addMouseMoveListener(fn: (ev: MouseEvent) => void) {
    this.mouseMoveListeners.push(fn)
  }

  public addMouseMoveLeave(fn: (ev: MouseEvent) => void) {
    this.mouseLeaveListeners.push(fn)
  }

  public setLive(live: boolean) {
    if (live === this.live) return

    this.live = live
    if (live) requestAnimationFrame(this.execute)
  }

  public updateFrame(frame: number) {
    this.frame = frame
  }

  public updateBoundaries(updated: IChartBoundaries) {
    this.boundaries = updated
  }

  public execute = (time: DOMHighResTimeStamp) => {
    if (!this.executable.length || !this.canvasRef.current) return
    if (!this.shouldUpdate()) return requestAnimationFrame(this.execute)

    this.ctx.clearRect(0, 0, this.canvasRef.current.width, this.canvasRef.current.height)

    if (this.live) {
      let dt = time - this.lastExecute
      if (Number.isNaN(dt)) dt = 0
      this.lastExecute = time
      this.tsFrom += dt
      this.tsTo += dt
    }

    for (const fn of this.executable) {
      fn(this.ctx, this.dataRef.current, this.wrapperRef, this.tsFrom, this.tsTo)
    }
    this.animationFrame = requestAnimationFrame(this.execute)
  }

  public destroy = () => {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame)
    }

    this.executable = []
    this.ctx.clearRect(0, 0, this.canvasRef.current.width, this.canvasRef.current.height)
    this.setLive(false)
  }

  public updateTimeInterval = (tsFrom: number, tsTo: number) => {
    this.lastExecute = null
    this.tsFrom = tsFrom
    this.tsTo = tsTo
  }

  // при кадре >30с больше половины из них вызовут изменение
  // интерфейса меньше 1px, поэтому можно обновлять компонент с меньшей частотой
  private shouldUpdate() {
    if (!this.lastExecute) return true

    // минимальное изменение времени которое вызовет сдвиг на 1/4 пикселя
    // 1/4 - для более гладкого движения, т.к. при 1 пикселе заметны рывки
    const dt_min = this.frame / (4 * this.boundaries.width)
    const now = performance.now()
    const dt = now - this.lastExecute

    return dt >= dt_min
  }
}

const defaultBoundaries: IChartBoundaries = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  width: 300,
  height: 100,
}

export default Chart
